import { NgIf, NgClass } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { Router } from '@angular/router';
import { AIRService } from '../../../services/air.service';

// Define the interface for status mapping
interface StatusMapping {
  status: string;
  cssClass: string;
}

@Component({
    selector: 'app-air-removed-tab',
    imports: [TableModule, DropdownModule, FormsModule, NgIf, NgClass],
    templateUrl: './air-removed-tab.component.html',
    styleUrl: './air-removed-tab.component.scss'
})
export class AirRemovedTabComponent implements OnChanges {
  @Input() airAPIData: any = '';

  // Initialize with empty array - will be populated from API
  removedData: any[] = [];

  // Initialize empty array for dropdown data
  dropDownData: any[] = [];
  selectedDivision: any = null;

  // Store original data for filtering
  private originaRemovedData: any[] = [];

  // Loading state for API calls
  loading: boolean = false;

  constructor(private router: Router, private airService: AIRService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['airAPIData']) {
      if (this.airAPIData) {
        this.processAirData();
      }
    }
  }

  /**
   * Processes the received API data for both table and dropdown
   */
  private processAirData(): void {
    // Process dropdown data first
    if (this.airAPIData &&
      this.airAPIData.airOverviewData &&
      this.airAPIData.airOverviewData.divisionDropDownRemovedList &&
      this.airAPIData.airOverviewData.divisionDropDownRemovedList.divisionRemovedNames) {

      const divisionData = this.airAPIData.airOverviewData.divisionDropDownRemovedList.divisionRemovedNames;

      if (Array.isArray(divisionData) && divisionData.length > 0) {
        // Transform the data to ensure it has the correct structure for PrimeNG dropdown
        this.dropDownData = divisionData.map((item: any) => {

          // Try different property combinations that might exist in the API response
          if (item && typeof item === 'object') {
            if (item.name !== undefined && item.value !== undefined) {
              return { label: item.name, value: item.value };
            } else if (item.label !== undefined && item.value !== undefined) {
              // If it already has label and value properties
              return item;
            } else if (item.division_name !== undefined) {
              // If it has division_name property
              return { label: item.division_name, value: item.division_name };
            } else {
              // Fallback - use first property as both label and value
              const firstKey = Object.keys(item)[0];
              const value = item[firstKey] || 'Unknown';
              return { label: value, value: value };
            }
          } else if (item && typeof item === 'string') {
            // If the item is just a string
            return { label: item, value: item };
          } else {
            // Fallback for unexpected item format
            return { label: 'Unknown', value: 'Unknown' };
          }
        });

        // Set default selection to first item
        if (this.dropDownData.length > 0) {
          // Access the value property from the first item
          this.selectedDivision = this.dropDownData[0].value;
        }
      }
    }

    // Process table data
    if (
      this.airAPIData.airOverviewData.airAllData.AIRAllRemoved) {
      const tableData = this.airAPIData.airOverviewData.airAllData.AIRAllRemoved;
      if (Array.isArray(tableData)) {
        this.originaRemovedData = tableData;
        this.processTableData(tableData);
      }
    }

    // Get data for the initially selected division
    if (this.selectedDivision) {
      this.filterInstalledData();
    }
  }

  /**
   * Maps a TLR status code to a user-friendly status label and CSS class
   * @param statusCode The TLR status code (I, P, R, etc.)
   * @returns Object containing the formatted status and CSS class
   */
  mapStatusAndCssClass(statusCode: string): StatusMapping {
    const statusCode2 = statusCode || '';
    let status = '';
    let cssClass = '';

    if (statusCode2 === 'I') {
      status = 'Installed';
      cssClass = 'green';
    } else if (statusCode2 === 'P') {
      status = 'Pre Installation';
      cssClass = 'blue';
    } else if (statusCode2 === 'R') {
      status = 'Removed';
      cssClass = 'red';
    } else {
      // Default case
      status = statusCode2;
      cssClass = '';
    }

    return { status, cssClass };
  }

  // Process the table data from API response
  processTableData(data: any[]): void {
    if (Array.isArray(data) && data.length > 0) {
      // Map API data to the format needed for the table
      this.originaRemovedData = data;
      this.removedData = data.map((item: any) => {
        // Use the centralized method to get status and CSS class
        const { status, cssClass } = this.mapStatusAndCssClass(item.tlr_status);

        return {
          division: item.division_name || '',
          section: item.section_name || '',
          tlr: item.tag_number || '',
          status: status,
          cssClass: cssClass
        };
      });

    } else {
      console.warn('Unable to process table data: data is empty or not an array');
    }
  }

  // Process dropdown data directly from API format
  processDropdownDataFromAPI(dropdownData: any): void {
    if (dropdownData.divisionRemovedNames && Array.isArray(dropdownData.divisionRemovedNames)) {
      this.dropDownData = dropdownData.divisionRemovedNames.map((item: any) => {
        if (item.name !== undefined && item.value !== undefined) {
          return { label: item.name, value: item.value };
        } else if (item.label !== undefined && item.value !== undefined) {
          return item;
        } else if (item.division_name !== undefined) {
          return { label: item.division_name, value: item.division_name };
        } else if (typeof item === 'string') {
          return { label: item, value: item };
        } else {
          // Fallback - use first property as both label and value
          const firstKey = Object.keys(item)[0];
          const value = item[firstKey] || 'Unknown';
          return { label: value, value: value };
        }
      });

      // Set default selection to "All" if data exists
      if (this.dropDownData.length > 0) {
        this.selectedDivision = 'All';
      }
    } else {
      if (this.originaRemovedData.length > 0) {
        this.processDropdownDataFromTable(this.originaRemovedData);
      }
    }
  }

  // Extract unique division names from table data (backup method)
  processDropdownDataFromTable(data: any[]): void {

    if (Array.isArray(data) && data.length > 0) {
      // Get unique division names
      const uniqueDivisions = [...new Set(data.map(item => item.division_name))];

      // Map to dropdown format
      this.dropDownData = uniqueDivisions
        .filter(division => division) // Filter out null/undefined values
        .map(division => {
          return {
            label: division,
            value: division
          };
        });


      // Set default selection to "All"
      if (this.dropDownData.length > 0) {
        this.selectedDivision = 'All';
      }
    }
  }

  filterInstalledData(): void {
    if (!this.selectedDivision) {
      return;
    }
    // Show loading indicator
    this.loading = true;

    // If selectedDivision is an object, extract the value
    const divisionValue = typeof this.selectedDivision === 'object' && this.selectedDivision !== null
      ? this.selectedDivision.value || this.selectedDivision.name || this.selectedDivision
      : this.selectedDivision;
    // Get data from API based on selected division
    this.airService.getDataBasedONDropdownList(divisionValue,'Removed')
      .subscribe({
        next: (response) => {
          if (response && Array.isArray(response)) {
            this.removedData = response.map((item: any) => {
              const { status, cssClass } = this.mapStatusAndCssClass(item.tlr_status);

              return {
                division: item.division_name || '',
                section: item.section_name || '',
                tlr: item.tag_number || '',
                status: status,
                cssClass: cssClass
              };
            });
          } else {
            this.clientSideFilter();
          }

          this.loading = false;
        },
        error: (error) => {
          this.clientSideFilter();
          this.loading = false;
        }
      });
  }

  // Fallback to client-side filtering if API call fails
  clientSideFilter(): void {
    if (!this.originaRemovedData) return;

    // Filter the data based on selected division
    const filteredData = this.originaRemovedData.filter((item: any) =>
      item.division_name === this.selectedDivision
    );

    // Update table with filtered data
    this.removedData = filteredData.map((item: any) => {
      // Use the centralized method to get status and CSS class
      const { status, cssClass } = this.mapStatusAndCssClass(item.tlr_status);

      return {
        division: item.division_name || '',
        section: item.section_name || '',
        tlr: item.tag_number || '',
        status: status,
        cssClass: cssClass
      };
    });
  }

  details(row: any): void {
    this.router.navigate(['/details-section', row.tlr]);
  }



  /**
   * Handles dropdown change event
   */
  onDivisionChange(): void {
    if (this.selectedDivision !== null && this.selectedDivision !== undefined) {
      // Call API with the selected value
      this.filterInstalledData();
    } else {
      console.warn('Selected division is null or undefined');
    }
  }
}
